import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TranslationProvider, useTranslation } from "./contexts/TranslationContext";
import { Toaster } from "./components/ui/sonner";
import { PageLoader } from "./components/LoadingSpinner";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import CampaignLandingPage from "./pages/CampaignLandingPage";
import LandingRouter from "./pages/LandingRouter";
import ArtworkSharePage from "./pages/ArtworkSharePage";
import UploadInviteRedeemPage from "./pages/UploadInviteRedeemPage";
import DashboardPage from "./pages/DashboardPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import CustomerOrderDetailPage from "./pages/CustomerOrderDetailPage";
import OrderProductPage from "./pages/OrderProductPage";
import OrderFilesPage from "./pages/OrderFilesPage";
import SonyUploadJobsPage from "./pages/SonyUploadJobsPage";
import SimpleMediaOrderPage from "./pages/SimpleMediaOrderPage";
import TranslationsPage from "./pages/TranslationsPage";
import UsersPage from "./pages/UsersPage"; // eslint-disable-line no-unused-vars -- rendered inside CustomersHubPage
import CustomersHubPage from "./pages/admin/CustomersHubPage";
import UserDetailPage from "./pages/UserDetailPage";
import CompanyTeamPage from "./pages/CompanyTeamPage";
import BillingProfilePage from "./pages/BillingProfilePage";
import PartnersPage from "./pages/PartnersPage";
import ReproPortalPage from "./pages/ReproPortalPage";
import ReproJobDetailPage from "./pages/ReproJobDetailPage";
import CustomerArtworkReviewPage from "./pages/CustomerArtworkReviewPage";
import PartnerRegisterPage from "./pages/PartnerRegisterPage";
import PricingPage from "./pages/PricingPage";
import CatalogueDetailPage from "./pages/CatalogueDetailPage";
import CatalogDetailPage from "./pages/CatalogDetailPage";
import NewCatalogPage from "./pages/NewCatalogPage";
import CustomerPricingPage from "./pages/CustomerPricingPage";
import InboundDocumentsPage from "./pages/InboundDocumentsPage";
import CompaniesPage from "./pages/CompaniesPage";
import ConfigureFamilyPage from "./pages/ConfigureFamilyPage";
import AdminCatalogueEntryPage from "./pages/AdminCatalogueEntryPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import CataloguePage from "./pages/CataloguePage";
import CatalogueIntegrationsPage from "./pages/CatalogueIntegrationsPage";
import ShopifyOAuthCallbackPage from "./pages/ShopifyOAuthCallbackPage";
import VisualBuilderAssetsPage from "./pages/VisualBuilderAssetsPage";
import BOMManagementPage from "./pages/BOMManagementPage";
import ProductConfigFlowsPage from "./pages/admin/ProductConfigFlowsPage";
import QuickOrderBundlesPage from "./pages/admin/QuickOrderBundlesPage";
import AttributeListsPage from "./pages/admin/AttributeListsPage";
import CatalogueSkuBackfillPage from "./pages/admin/CatalogueSkuBackfillPage";
import ExchangeRatesPage from "./pages/admin/ExchangeRatesPage";
import OverviewPage from "./pages/admin/OverviewPage";
import NcbReportPage from "./pages/admin/NcbReportPage";
// TEMPORARY — Q3-2026 VAT investigation. Remove this import + the
// /admin/vat-diagnostic route below once the investigation is closed.
import VatDiagnosticPage from "./pages/admin/VatDiagnosticPage";
import SystemSettingsPage from "./pages/admin/SystemSettingsPage";
import ArtworkTemplatesAdminPage from "./pages/admin/ArtworkTemplatesAdminPage";
import ArtworkTemplatesLibraryPage from "./pages/ArtworkTemplatesLibraryPage";
import PublicUploadPage from "./pages/PublicUploadPage";
import FinanceDashboardPage from "./pages/FinanceDashboardPage";
import OperatingExpensesPage from "./pages/OperatingExpensesPage";
import AuthCallback from "./pages/AuthCallback";
import MagicLinkCallbackPage from "./pages/MagicLinkCallbackPage";
import EmailLogPage from "./pages/EmailLogPage";
import SystemLogsPage from "./pages/SystemLogsPage";
import EmailTemplatesPage from "./pages/admin/EmailTemplatesPage";
import CustomerAcquisitionPage from "./pages/admin/CustomerAcquisitionPage"; // eslint-disable-line no-unused-vars -- rendered inside CampaignsHubPage; kept for direct imports elsewhere
import DistributionDemoLayout from "./pages/admin/distribution/DistributionDemoLayout";
import ReleaseToRetailPage from "./pages/admin/distribution/ReleaseToRetailPage";
import InventoryControlTowerPage from "./pages/admin/distribution/InventoryControlTowerPage";
import RetailOrderAutomationPage from "./pages/admin/distribution/RetailOrderAutomationPage";

import CustomerInvoicesPage from "./pages/CustomerInvoicesPage";
import CampaignsPanel from "./components/CampaignsPanel";
import CampaignsHubPage from "./pages/admin/CampaignsHubPage";
import { resolveLandingPath } from "./lib/landingPath";
import { ErrorBoundary } from "./components/ErrorBoundary";
import AdminInvoiceFinancePage from "./pages/AdminInvoiceFinancePage";
import SupplierReconciliationPage from "./pages/SupplierReconciliationPage";
import ReconciliationDiagnosticPage from "./pages/ReconciliationDiagnosticPage";

// Sync admin status to translation context for missing key auto-capture
const AdminStatusSync = () => {
    const { isAdmin, user, isAuthenticated } = useAuth();
    const { setIsAdmin, setCurrentUserLanguageContext } = useTranslation();

    useEffect(() => {
        setIsAdmin(isAdmin);
    }, [isAdmin, setIsAdmin]);

    // Push the auth'd user's language context into TranslationContext so
    // priorities 1 (user preferred_language) and 3 (company default_language)
    // are honoured immediately on login / company switch / /auth/me refresh.
    // See TranslationContext.resolveLanguage for the priority chain.
    useEffect(() => {
        setCurrentUserLanguageContext({
            preferred_language: user?.preferred_language || null,
            company_default_language: user?.active_company_default_language || null,
            isAuthenticated: !!isAuthenticated,
        });
    }, [
        isAuthenticated,
        user?.preferred_language,
        user?.active_company_default_language,
        setCurrentUserLanguageContext,
    ]);

    return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();
    const location = useLocation();

    if (loading) {
        return <PageLoader />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Guest upload-link sessions are locked to their single order page.
    if (user?.role === 'guest') {
        const guestRaw = localStorage.getItem('guest_session');
        const guest = guestRaw ? JSON.parse(guestRaw) : null;
        const allowedPath = guest?.order_id ? `/orders/${guest.order_id}` : null;
        if (allowedPath && !location.pathname.startsWith(allowedPath)) {
            return <Navigate to={`${allowedPath}/files?guest=1`} replace />;
        }
    }

    return children;
};

// Translator/Admin Route Component
const TranslatorRoute = ({ children }) => {
    const { isAuthenticated, loading, canAccessTranslations } = useAuth();

    if (loading) {
        return <PageLoader />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (!canAccessTranslations) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Repro Partner Route (also allows admins)
const ReproPartnerRoute = ({ children }) => {
    const { user, isAuthenticated, loading, isAdmin } = useAuth();

    if (loading) {
        return <PageLoader />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Allow admins and repro_partner role
    if (!isAdmin && user?.role !== 'repro_partner') {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Admin Only Route Component
const AdminRoute = ({ children }) => {
    const { isAuthenticated, loading, isAdmin } = useAuth();

    if (loading) {
        return <PageLoader />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (!isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Super Admin Route Component
const SuperAdminRoute = ({ children }) => {
    const { isAuthenticated, loading, isSuperAdmin } = useAuth();

    if (loading) {
        return <PageLoader />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (!isSuperAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Public Route Component (redirects to appropriate landing page if already logged in)
const PublicRoute = ({ children }) => {
    const { user, isAuthenticated, loading } = useAuth();

    if (loading) {
        return <PageLoader />;
    }

    if (isAuthenticated) {
        // Honour the user's preferred landing page — same helper used by
        // LoginPage after a successful submit. Without this PublicRoute
        // would race with LoginPage.handleSubmit and force everyone to
        // /dashboard regardless of their default_landing_page choice.
        return <Navigate to={resolveLandingPath(user)} replace />;
    }

    return children;
};

// Dashboard route - redirects repro partners to artwork-jobs
const DashboardRoute = ({ children }) => {
    const { user, isAuthenticated, loading } = useAuth();

    if (loading) {
        return <PageLoader />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Repro partners should not access dashboard
    if (user?.role === 'repro_partner') {
        return <Navigate to="/artwork-jobs" replace />;
    }

    return children;
};

// Home redirect based on role
const HomeRedirect = () => {
    const { user, isAuthenticated, loading } = useAuth();

    if (loading) {
        return <PageLoader />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Redirect repro partners to artwork-jobs, others to dashboard
    const homeRoute = user?.role === 'repro_partner' ? '/artwork-jobs' : '/dashboard';
    return <Navigate to={homeRoute} replace />;
};

// Role-based Order Detail Route
const OrderDetailRoute = () => {
    const { isAdmin, user } = useAuth();
    // Guest upload-link sessions ALWAYS render the customer view — same UX
    // as the order owner, but heavily filtered by route guards inside it.
    // Wrapped in ErrorBoundary so a single corrupt order can't blank the page.
    const inner = user?.role === 'guest'
        ? <CustomerOrderDetailPage />
        : (isAdmin ? <OrderDetailPage /> : <CustomerOrderDetailPage />);
    return (
        <ErrorBoundary
            title="Order page could not be loaded"
            description="A data condition on this order prevented the page from rendering. Other orders are not affected. The error has been logged for investigation."
        >
            {inner}
        </ErrorBoundary>
    );
};

function AppRoutes() {
    // CRITICAL: Detect Emergent OAuth #session_id= synchronously during render,
    // BEFORE any route/auth check runs. This prevents race conditions.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/" element={<HomeRedirect />} />
            {/* Public campaign landing pages — visitors land here directly from external marketing,
                Quick Order flow + login wall. Allowed both signed-in and signed-out. */}
            <Route path="/campaigns/:slug" element={<CampaignLandingPage />} />
            <Route path="/start/:slug" element={<CampaignLandingPage />} />
            {/* Public Landing Pages — permanent marketing entry points.
                Same anonymous-to-signup flow as Campaigns but without the
                bundle whitelist. Both `/l/{slug}` and `/landing/{slug}`
                are accepted so marketing can pick the shorter one. */}
            <Route path="/l/:slug" element={<LandingRouter />} />
            <Route path="/landing/:slug" element={<LandingRouter />} />
            <Route path="/artwork-share/:token" element={<ArtworkSharePage />} />
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/register"
                element={
                    <PublicRoute>
                        <RegisterPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/register/:slug"
                element={
                    <PublicRoute>
                        <RegisterPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/forgot-password"
                element={
                    <PublicRoute>
                        <ForgotPasswordPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/reset-password"
                element={
                    <PublicRoute>
                        <ResetPasswordPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/auth/magic"
                element={<MagicLinkCallbackPage />}
            />
            <Route
                path="/partner-register/:token"
                element={<PartnerRegisterPage />}
            />
            {/* Public upload link - no auth required */}
            <Route
                path="/upload/:token"
                element={<UploadInviteRedeemPage />}
            />
            <Route
                path="/dashboard"
                element={
                    <DashboardRoute>
                        <DashboardPage />
                    </DashboardRoute>
                }
            />
            <Route
                path="/orders/:orderId"
                element={
                    <ProtectedRoute>
                        <OrderDetailRoute />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/orders/:orderId/product"
                element={
                    <ProtectedRoute>
                        <OrderProductPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/orders/new/product"
                element={
                    <ProtectedRoute>
                        <OrderProductPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/orders/new/simple"
                element={
                    <ProtectedRoute>
                        <SimpleMediaOrderPage />
                    </ProtectedRoute>
                }
            />
            {/* Phase 3: family-aware configurator entry-point (CD/DVD/Blu-ray).
                Vinyl continues to use /orders/new/product — untouched. */}
            <Route
                path="/configure/:family"
                element={
                    <ProtectedRoute>
                        <ConfigureFamilyPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/orders/:orderId/files"
                element={
                    <ProtectedRoute>
                        <ErrorBoundary
                            title="Order files page could not be loaded"
                            description="A data condition on this order's files (artwork, master, or supplier records) prevented the page from rendering. Other orders are not affected. The error has been logged for investigation."
                        >
                            <OrderFilesPage />
                        </ErrorBoundary>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/translations"
                element={
                    <TranslatorRoute>
                        <TranslationsPage />
                    </TranslatorRoute>
                }
            />
            <Route
                path="/admin/customers"
                element={
                    <AdminRoute>
                        <CustomersHubPage />
                    </AdminRoute>
                }
            />
            {/* Back-compat: pre-2026-02 admin URLs. Detail views for a
                specific company/user keep their own routes below. */}
            <Route path="/users" element={<Navigate to="/admin/customers?tab=users" replace />} />
            <Route
                path="/users/:userId"
                element={
                    <AdminRoute>
                        <UserDetailPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/team"
                element={
                    <ProtectedRoute>
                        <CompanyTeamPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <UserDetailPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/partners"
                element={
                    <AdminRoute>
                        <PartnersPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/orders/:orderId/artwork-review"
                element={
                    <ProtectedRoute>
                        <ErrorBoundary
                            title="Artwork review could not be loaded"
                            description="A data condition prevented this artwork review page from rendering. Other orders are not affected. The error has been logged for investigation."
                        >
                            <CustomerArtworkReviewPage />
                        </ErrorBoundary>
                    </ProtectedRoute>
                }
            />
            {/* Finance Routes - Super Admin Only */}
            <Route
                path="/finance"
                element={
                    <SuperAdminRoute>
                        <FinanceDashboardPage />
                    </SuperAdminRoute>
                }
            />
            <Route
                path="/finance/expenses"
                element={
                    <SuperAdminRoute>
                        <OperatingExpensesPage />
                    </SuperAdminRoute>
                }
            />
            <Route
                path="/finance/invoices"
                element={
                    <AdminRoute>
                        <AdminInvoiceFinancePage />
                    </AdminRoute>
                }
            />
            <Route
                path="/finance/reconciliation"
                element={
                    <AdminRoute>
                        <SupplierReconciliationPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/finance/reconciliation/diagnostic"
                element={
                    <AdminRoute>
                        <ReconciliationDiagnosticPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/billing"
                element={
                    <ProtectedRoute>
                        <BillingProfilePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/invoices"
                element={
                    <ProtectedRoute>
                        <CustomerInvoicesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/catalogue"
                element={
                    <ProtectedRoute>
                        <CataloguePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/catalogue/integrations"
                element={
                    <ProtectedRoute>
                        <CatalogueIntegrationsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/auth/shopify/callback"
                element={<ShopifyOAuthCallbackPage />}
            />
            <Route
                path="/catalogue/:id"
                element={
                    <ProtectedRoute>
                        <CatalogueDetailPage />
                    </ProtectedRoute>
                }
            />
            {/* Artwork Portal Routes - for repro_partner role and admins */}
            <Route
                path="/artwork-jobs"
                element={
                    <ReproPartnerRoute>
                        <ReproPortalPage />
                    </ReproPartnerRoute>
                }
            />
            <Route
                path="/artwork-jobs/:jobId"
                element={
                    <ReproPartnerRoute>
                        <ReproJobDetailPage />
                    </ReproPartnerRoute>
                }
            />
            {/* Pricing System Routes - Admin Only */}
            <Route
                path="/admin/pricing"
                element={
                    <AdminRoute>
                        <PricingPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/sony-upload-jobs"
                element={
                    <AdminRoute>
                        <SonyUploadJobsPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/customer-acquisition"
                element={<Navigate to="/admin/campaigns?tab=acquisition" replace />}
            />
            <Route
                path="/finance/cashflow-forecast"
                element={<Navigate to="/finance?tab=cashflow" replace />}
            />
            <Route
                path="/admin/pricing/catalogs/new"
                element={
                    <AdminRoute>
                        <NewCatalogPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/pricing/catalogs/:catalogId"
                element={
                    <AdminRoute>
                        <CatalogDetailPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/pricing/customers/:organizationId"
                element={
                    <AdminRoute>
                        <CustomerPricingPage />
                    </AdminRoute>
                }
            />
            {/* Inbound Documents Route - Admin Only */}
            <Route
                path="/admin/inbound-documents"
                element={
                    <AdminRoute>
                        <InboundDocumentsPage />
                    </AdminRoute>
                }
            />
            {/* NCB Monthly Report — Admin only */}
            <Route
                path="/admin/reports/ncb"
                element={
                    <AdminRoute>
                        <NcbReportPage />
                    </AdminRoute>
                }
            />
            {/* TEMPORARY — Q3-2026 VAT investigation; remove after closure */}
            <Route
                path="/admin/vat-diagnostic"
                element={
                    <AdminRoute>
                        <VatDiagnosticPage />
                    </AdminRoute>
                }
            />
            {/* Companies Route - Admin Only. List view redirects to
                the Customers hub; detail views keep their own paths. */}
            <Route
                path="/admin/companies"
                element={<Navigate to="/admin/customers?tab=companies" replace />}
            />
            <Route
                path="/admin/companies/:companyId"
                element={
                    <AdminRoute>
                        <CompaniesPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/companies/:companyId/catalogue/:entryId"
                element={
                    <AdminRoute>
                        <AdminCatalogueEntryPage />
                    </AdminRoute>
                }
            />
            {/* Integrations Routes - Admin Only */}
            <Route
                path="/admin/integrations"
                element={
                    <AdminRoute>
                        <IntegrationsPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/integrations/:integrationId"
                element={
                    <AdminRoute>
                        <IntegrationsPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/settings"
                element={
                    <AdminRoute>
                        <SystemSettingsPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/artwork-templates"
                element={
                    <AdminRoute>
                        <ArtworkTemplatesAdminPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/artwork-templates"
                element={
                    <ProtectedRoute>
                        <ArtworkTemplatesLibraryPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/email-log"
                element={
                    <AdminRoute>
                        <EmailLogPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/system-logs"
                element={
                    <AdminRoute>
                        <SystemLogsPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/email-templates"
                element={<Navigate to="/admin/settings?tab=email&sub=templates" replace />}
            />
            <Route
                path="/admin/visual-builder"
                element={
                    <AdminRoute>
                        <VisualBuilderAssetsPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/bom"
                element={
                    <AdminRoute>
                        <BOMManagementPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/product-config"
                element={
                    <AdminRoute>
                        <ProductConfigFlowsPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/quick-order-bundles"
                element={
                    <AdminRoute>
                        <QuickOrderBundlesPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/attribute-lists"
                element={
                    <AdminRoute>
                        <AttributeListsPage />
                    </AdminRoute>
                }
            />
            {/* Temporary utility — delete alongside CatalogueSkuBackfillPage
                and routes/catalogue_sku_backfill.py once the backfill is done. */}
            <Route
                path="/admin/catalogue-sku-backfill"
                element={
                    <SuperAdminRoute>
                        <CatalogueSkuBackfillPage />
                    </SuperAdminRoute>
                }
            />
            <Route
                path="/admin/exchange-rates"
                element={
                    <AdminRoute>
                        <ExchangeRatesPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/overview"
                element={
                    <AdminRoute>
                        <OverviewPage />
                    </AdminRoute>
                }
            />
            <Route
                path="/admin/campaigns"
                element={
                    <AdminRoute>
                        <CampaignsHubPage />
                    </AdminRoute>
                }
            />
            {/* Owntune Distribution — Phase 0 Demo (super-admin only, mock data).
                Three connected screens share one in-session state. No DB writes. */}
            <Route
                path="/admin/distribution"
                element={
                    <SuperAdminRoute>
                        <DistributionDemoLayout />
                    </SuperAdminRoute>
                }
            >
                <Route index element={<Navigate to="/admin/distribution/release" replace />} />
                <Route path="release" element={<ReleaseToRetailPage />} />
                <Route path="inventory" element={<InventoryControlTowerPage />} />
                <Route path="automation" element={<RetailOrderAutomationPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    // Capture first-touch acquisition data (UTM params + referrer) on app
    // boot so it survives navigation and is available when the user
    // signs up later.
    useEffect(() => {
        import('./lib/acquisition').then(m => m.captureAcquisitionOnce());
    }, []);

    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <TranslationProvider>
                        <AdminStatusSync />
                        <ImpersonationBanner />
                        <AppRoutes />
                        <Toaster position="top-right" richColors />
                    </TranslationProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
